import React from 'react';
import Axios, { AxiosResponse, AxiosRequestConfig } from 'axios';
import qs from 'qs';
import comparator from 'ramda/es/comparator';
import { FormattedMessage } from 'react-intl';

const axios = Axios.create({ baseURL: '/support/api' });

interface Response<T> {
  code: number;
  description: string;
  data: T;
}

export const ErrorMessage: React.FC<{error: RequestError | Error}> = ({ error }) => {
  if (error instanceof RequestError) {
    return <FormattedMessage id={`Error.${error.code}`} />
  }

  if (error instanceof JSONParseError) {
    return (
      <>
        {error.message}
        <br />
        <br />
        <code>{error.text}</code>
      </>
    )
  }

  return (
    <>
      {error.message}
    </>
  );
}

export class RequestError extends Error {
  public code: number;

  constructor(code: number, message: string) {
    super(message);
    this.name = `RequestError:${code}`;
    this.code = code;
  }
}

export class JSONParseError extends SyntaxError {
  constructor(message: string, readonly text: string) {
    super(message);
  }
}

export class StatusError extends Error {
  public response: AxiosResponse;

  constructor(response: AxiosResponse) {
    super(response.statusText);
    this.name = 'StatusError';
    this.response = response;
  }
}

function throwIfNotOk<T>(response: AxiosResponse<Response<T>>) {
  if (response.status !== 200) {
    throw new StatusError(response);
  }
  return response.data;
}

function throwIfFailure<T>({ code, description, data }: Response<T>) {
  if (code !== 0) {
    throw new RequestError(code, description);
  }
  return data;
}

export type Interface<P = any, R = any> = (params: P, config?: AxiosRequestConfig) => Promise<R>;
export type InterfaceParams<I> = I extends Interface<infer P, any> ? P : never;
export type InterfaceResponse<I> = I extends Interface<any, infer R> ? R : never;

function api<T, R>(
  request: (
    params: T,
    config?: AxiosRequestConfig
  ) => Promise<AxiosResponse<Response<R>>>,
) {
  return (params: T, config?: AxiosRequestConfig) =>
    request(params, config).then(throwIfNotOk).then(throwIfFailure);
}

function map<I extends Interface, R>(fn: I, transform: (response: InterfaceResponse<I>) => R) {
  return async (params: InterfaceParams<I>, config?: AxiosRequestConfig) => {
    const response = await fn(params, config);
    return transform(response);
  };
}

const delay = (ms: number) => new Promise(
  (resolve) => setTimeout(resolve, ms),
);


function mock<P, R>(api: Interface<P, R>, fn: Interface<P, R>) {
  if (process.env.NODE_ENV === 'production') {
    return api;
  }

  return async (params: P, config?: AxiosRequestConfig) => {
    await delay(500);
    if (Math.random() < 0.5)  {
      throw new RequestError(
        10027,
        'This is an error message for testing, and this error message is detailed and helpful'
      );
    }
    return fn(params, config);
  };
}

export interface LocaleVersion {
  lang: string;
  name: string;
  description: string;
}

export interface CategoryItem {
  requestCategoryId: string;
  resource: LocaleVersion[];
}

function transformLocales<T extends {lang: string}>(locales: T[]) {
  return locales.reduce<Record<string, T>>(
    (acc, value) => {
      return {
        ...acc,
        [value.lang]: value,
      };
    },
    {},
  );
}

type Replace<T, U extends keyof any, V> = {[K in U]: V} & Omit<T, U>;

const parse = (text: string) => {
  try {
    return JSON.parse(text)
  } catch (error) {
    throw new JSONParseError(error.message, text)
  }
}

export const requestCategory = map(
  mock(
    map(
      api<void, (Replace<CategoryItem, 'resource', string>)[]>(() => axios.get('/requestCategory')),
      (items) => items.map(
        ({ requestCategoryId, resource }) => ({
          requestCategoryId,
          resource: parse(resource) as ResourceItem
        })
      )
    ),
    () => Promise.resolve([
      {
        requestCategoryId: '1',
        resource: {
          locales: [
            {
              lang: 'zh-cn',
              name: '行使您的数据权利',
              description: '您可以在此行使您的数据权利',
            },
            {
              lang: 'zh-tw',
              name: '行使您的數據權利',
              description: '您可以在此行使您的數據權利',
            },
            {
              lang: 'en',
              name: 'Excercise Your Data Rights',
              description: 'You can excercise your data rights here',
            },
          ],
          displayOrder: 0,
        }
      },
    ]),
  ),
  (items) => items.sort(compareDisplayOrder).map(
    ({ requestCategoryId, resource: { displayOrder, locales } }) =>
      ({
        requestCategoryId,
        resource: { displayOrder, locales: transformLocales(locales) }
      }),
  ),
);

export interface HelpText {
  lang: string;
  content: string;
  button: string;
  buttonUrl: string;
}

interface ResourceItem {
  displayOrder: number;
  locales: LocaleVersion[];
}

export interface TypeItem {
  requestCategoryId: string;
  requestTypeId: string;
  resource: ResourceItem;
  helpText: HelpText[];
}

const compareDisplayOrder = comparator<{ resource: ResourceItem }>(
  (a, b) => (a.resource.displayOrder < b.resource.displayOrder)
);

export const requestType = map(
  mock(
    map(
      api
      <
        {
          requestCategoryId: string;
          regionCode: string;
        },
        (Replace<TypeItem, 'helpText' | 'resource', string>)[]
      >
      (
        (params) => axios.get(`/requestType?${qs.stringify(params)}`)
      ),
      items => items.map(
        ({ helpText, resource, ...rest }) => {
          return {
            helpText: helpText ? parse(helpText) as HelpText[] : [],
            resource: parse(resource) as ResourceItem,
            ...rest
          }
        }
      )
    ),
    () => {
      const resource = {
        locales: [
          {
            lang: 'zh-cn',
            name: '访问您的数据（获取副本）',
            description: '您可以在此访问您的数据',
          },
          {
            lang: 'zh-tw',
            name: '訪問您的數據（獲取副本）',
            description: '您可以在此行訪問您的數據',
          },
          {
            lang: 'en',
            name: 'Access your data (get the data copy)',
            description: 'You can access your data here',
          },
        ],
        displayOrder: 0,
      };
      const helpText = [
        {
          lang: 'zh-cn',
          content: '### Title\n\n ![example](https://cdn.cnbj1.fds.api.mi-img.com/feedback/assets/example.jpg)',
          button: '联系小米客服',
          buttonUrl: '',
        },
        {
          lang: 'zh-tw',
          content: 'xxxxxxxxxxxx',
          button: '联系小米客服',
          buttonUrl: '',
        },
        {
          lang: 'en',
          content: 'xxxxxxxxxxxx',
          button: 'Contact Customer Service',
          buttonUrl: '',
        },
      ];
      return Promise.resolve([
        {
          requestCategoryId: '1',
          requestTypeId: '1',
          resource,
          helpText,
        },
        {
          requestCategoryId: '1',
          requestTypeId: '2',
          resource,
          helpText: []
        }
      ])
    },
  ),
  (items) => items.sort(compareDisplayOrder).map(
    ({ resource: { displayOrder, locales }, helpText, ...rest }) => ({
      resource: {
        displayOrder,
        locales: transformLocales(locales),
      },
      helpText: helpText.length === 0 ? null : transformLocales(helpText),
      ...rest,
    }),
  ),
);

export const sendMailCode = mock(
  api<{ mail: string, locale: string }, void>((params) => axios.post('/mail/sendCode', qs.stringify(params))),
  () => Promise.resolve(),
);


export const uploadPicture = mock(
  map(
    api<{ picture: File, requestCategoryId: string }, {
      temporaryAttachments: [
        {
          temporaryAttachmentId: string;
          fileName: string;
        }
      ];
    }>(
      async (
        { picture, requestCategoryId },
        config?: AxiosRequestConfig,
      ) => {
        const data = new FormData();
        data.set('picture', picture);
        data.set('requestCategoryId', requestCategoryId);
        return axios.post('/uploadPicture', data, config);
      },
    ),
    ({ temporaryAttachments: [{ temporaryAttachmentId }] }) => temporaryAttachmentId,
  ),
  ({ picture }, config = {}) => {
    const { onUploadProgress = () => {} } = config;
    const total = picture.size;
    let loaded = 0;
    return new Promise(
      (resolve) => {
        const timer = setInterval(
          () => {
            loaded = Math.min(total, loaded + 1024);
            onUploadProgress({ loaded, total });
            if (loaded === total) {
              clearInterval(timer);
              resolve(picture.name);
            }
          },
          50,
        );
      },
    );
  },
);

export interface CommitParams {
  regionCode: string,
  requestCategoryId: string;
  requestTypeId: string;
  product: string;
  summary: string;
  description: string;
  temporaryAttachmentId?: string[];
  userName: string;
  mail: string;
  account?: string;
  verifyCode: string;
}

export const commit = mock(
  api<CommitParams, void>(
    ({temporaryAttachmentId, ...rest}) => axios.post(
      '/commit',
      qs.stringify({
        temporaryAttachmentId: temporaryAttachmentId && temporaryAttachmentId.join(','),
        ...rest
      })
    )
  ),
  () => Promise.resolve(),
);
